import { forwardRef, useState, type InputHTMLAttributes, type ChangeEvent } from 'react';

import { cn } from '@/lib/utils';

export type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  showValue?: boolean;
  formatValue?: (value: number) => string;
};

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ label, showValue = true, formatValue, className = '', id, min = 0, max = 100, value, defaultValue, disabled, onChange, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const [internalValue, setInternalValue] = useState(defaultValue ?? min);
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    const displayValue = formatValue
      ? formatValue(Number(currentValue))
      : String(currentValue);

    const percentage = ((Number(currentValue) - Number(min)) / (Number(max) - Number(min))) * 100;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(Number(e.target.value));
      }
      onChange?.(e);
    };

    return (
      <div className={cn('flex flex-col gap-2', className)}>
        {(label || showValue) && (
          <div className="flex items-center justify-between">
            {label && (
              <label htmlFor={inputId} className="text-sm font-medium text-gray-300">
                {label}
              </label>
            )}
            {showValue && (
              <span className="text-sm font-medium text-brand-400 tabular-nums">
                {displayValue}
              </span>
            )}
          </div>
        )}
        <div className="relative flex items-center h-5">
          <div className="absolute inset-x-0 h-2 rounded-full bg-surface-700" />
          <div
            className="absolute left-0 h-2 rounded-full bg-brand-500"
            style={{ width: `${percentage}%` }}
          />
          <input
            ref={ref}
            id={inputId}
            type="range"
            min={min}
            max={max}
            value={currentValue}
            onChange={handleChange}
            disabled={disabled}
            className={cn(
              'relative w-full h-2 appearance-none cursor-pointer bg-transparent',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none',
              '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5',
              '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-500',
              '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-brand-300',
              '[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer',
              '[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150',
              '[&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:active:scale-95',
              '[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5',
              '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-brand-500',
              '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-brand-300 [&::-moz-range-thumb]:cursor-pointer',
              '[&::-webkit-slider-runnable-track]:bg-transparent [&::-moz-range-track]:bg-transparent',
            )}
            {...props}
          />
        </div>
      </div>
    );
  },
);

Slider.displayName = 'Slider';
